import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function VerificationAdmin({ user, adminUser, adminLevel }) {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [adminUser, adminLevel]);

  useEffect(() => {
    if (isAdmin) {
      loadVerifications();
    }
  }, [isAdmin]);

  const checkAdminAccess = async () => {
    try {
      if (adminUser && adminLevel) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const loadVerifications = async () => {
    try {
      console.log('Loading verifications...');
      
      // First, get all verifications
      const { data: verifications, error: verificationError } = await supabase
        .from('face_verifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (verificationError) {
        console.error('Verification query error:', verificationError);
        throw verificationError;
      }

      console.log('Raw verifications:', verifications);

      // Then get profile data for each verification
      const verificationsWithProfiles = await Promise.all(
        (verifications || []).map(async (verification) => {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('name, email, photo_url')
            .eq('id', verification.user_id)
            .single();

          if (profileError) {
            console.error('Profile query error for user:', verification.user_id, profileError);
          }

          return {
            ...verification,
            profiles: profile || { name: 'Unknown', email: 'Unknown', photo_url: null }
          };
        })
      );

      console.log('Verifications with profiles:', verificationsWithProfiles);
      setVerifications(verificationsWithProfiles);
    } catch (error) {
      console.error('Error loading verifications:', error);
      setVerifications([]);
    }
  };

  const updateVerification = async (verificationId, status, notes = '') => {
    setUpdating(verificationId);
    
    try {
      const { error } = await supabase
        .from('face_verifications')
        .update({
          status: status,
          admin_reviewed: true,
          admin_notes: notes,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id
        })
        .eq('id', verificationId);

      if (error) throw error;

      // Reload verifications to show updated status
      await loadVerifications();
      
      alert(`Verification ${status} successfully!`);
    } catch (error) {
      console.error('Error updating verification:', error);
      alert('Error updating verification: ' + error.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleApprove = (verificationId) => {
    const notes = prompt('Add approval notes (optional):');
    updateVerification(verificationId, 'verified', notes || 'Approved by admin');
  };

  const handleReject = (verificationId) => {
    const notes = prompt('Add rejection reason (required):');
    if (notes && notes.trim()) {
      updateVerification(verificationId, 'rejected', notes);
    } else {
      alert('Rejection reason is required.');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        color: 'white',
        fontSize: '18px',
        textAlign: 'center'
      }}>
        <div>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚫</div>
          <h2>Access Denied</h2>
          <p>You don't have admin permissions to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px 0'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Face Verification Admin
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            margin: 0
          }}>
            Review and approve user verification requests
          </p>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {['pending', 'verified', 'rejected'].map(status => {
            const count = verifications.filter(v => v.status === status).length;
            const colors = {
              pending: { bg: 'rgba(255, 193, 7, 0.1)', border: '#ffc107', text: '#ffc107' },
              verified: { bg: 'rgba(40, 167, 69, 0.1)', border: '#28a745', text: '#28a745' },
              rejected: { bg: 'rgba(220, 53, 69, 0.1)', border: '#dc3545', text: '#dc3545' }
            };
            
            return (
              <div key={status} style={{
                background: colors[status].bg,
                border: `1px solid ${colors[status].border}`,
                borderRadius: '15px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <h3 style={{
                  color: colors[status].text,
                  fontSize: '24px',
                  fontWeight: '600',
                  margin: '0 0 5px 0',
                  textTransform: 'capitalize'
                }}>
                  {count}
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  margin: 0,
                  textTransform: 'capitalize'
                }}>
                  {status}
                </p>
              </div>
            );
          })}
        </div>

        {/* Verification List */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '15px',
          padding: '25px'
        }}>
          <h3 style={{
            color: '#00E9FF',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '15px'
          }}>
            Verification Requests
          </h3>
          
          <div style={{
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: 0,
              color: '#ffc107',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              📋 Check verification photos for:
            </p>
            <ul style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '13px',
              lineHeight: '1.5',
              marginTop: '10px',
              marginBottom: 0,
              paddingLeft: '20px'
            }}>
              <li>Same person in profile photo vs verification photo</li>
              <li>Clear, readable handwritten paper with "Flirteen + date + time"</li>
              <li>No filters, face coverings, or photo editing</li>
              <li>Good lighting and photo quality</li>
            </ul>
          </div>

          {verifications.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '16px',
              padding: '40px'
            }}>
              No verification requests found.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '20px'
            }}>
              {verifications.map((verification) => (
                <div key={verification.id} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '15px',
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 200px 200px 1fr auto',
                    gap: '20px',
                    alignItems: 'center'
                  }}>
                    {/* User Info */}
                    <div>
                      <h4 style={{
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 5px 0'
                      }}>
                        {verification.profiles?.name || 'Unknown User'}
                      </h4>
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '12px',
                        margin: '0 0 5px 0'
                      }}>
                        {verification.profiles?.email}
                      </p>
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '12px',
                        margin: 0
                      }}>
                        Submitted: {new Date(verification.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Profile Photo */}
                    <div style={{ textAlign: 'center' }}>
                      <p style={{
                        color: '#00E9FF',
                        fontSize: '12px',
                        fontWeight: '600',
                        margin: '0 0 10px 0'
                      }}>
                        Profile Photo
                      </p>
                      <img
                        src={verification.profile_photo_url}
                        alt="Profile"
                        style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '10px',
                          objectFit: 'cover',
                          border: '2px solid rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Verification Photo */}
                    <div style={{ textAlign: 'center' }}>
                      <p style={{
                        color: '#00E9FF',
                        fontSize: '12px',
                        fontWeight: '600',
                        margin: '0 0 10px 0'
                      }}>
                        Verification Photo
                      </p>
                      <img
                        src={verification.verification_photo_url}
                        alt="Verification"
                        style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '10px',
                          objectFit: 'cover',
                          border: '2px solid rgba(255, 255, 255, 0.2)'
                        }}
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: verification.status === 'verified' ? '#28a745' :
                                   verification.status === 'rejected' ? '#dc3545' : '#ffc107',
                        color: 'white',
                        textTransform: 'capitalize'
                      }}>
                        {verification.status}
                      </span>
                      
                      {verification.admin_notes && (
                        <p style={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '12px',
                          margin: '10px 0 0 0',
                          fontStyle: 'italic'
                        }}>
                          "{verification.admin_notes}"
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    {verification.status === 'pending' && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                      }}>
                        <button
                          onClick={() => handleApprove(verification.id)}
                          disabled={updating === verification.id}
                          style={{
                            padding: '8px 16px',
                            background: updating === verification.id ? 
                              'rgba(255, 255, 255, 0.3)' : 
                              'linear-gradient(90deg, #28a745, #20c997)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: updating === verification.id ? 'not-allowed' : 'pointer',
                            fontFamily: 'Poppins, sans-serif'
                          }}
                        >
                          {updating === verification.id ? 'Processing...' : 'Approve'}
                        </button>
                        
                        <button
                          onClick={() => handleReject(verification.id)}
                          disabled={updating === verification.id}
                          style={{
                            padding: '8px 16px',
                            background: updating === verification.id ? 
                              'rgba(255, 255, 255, 0.3)' : 
                              'linear-gradient(90deg, #dc3545, #c82333)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: updating === verification.id ? 'not-allowed' : 'pointer',
                            fontFamily: 'Poppins, sans-serif'
                          }}
                        >
                          {updating === verification.id ? 'Processing...' : 'Reject'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerificationAdmin;